import type {
  AiHandoffFigure,
  AiHandoffLandPayload,
  LandAnalysisSectionId,
} from '@popety-io/types'
import { buildSections, emptySections, specIds } from './sectionValues'
import { LAND_SECTION_SPECS } from './landSectionFields'
import { aiHandoffAbout } from './about'
import { AI_HANDOFF_CONNECT_URL } from './constants'

/**
 * Every recognised land section id. `sections_omitted` is filtered against this
 * set because the stored selection reaches us straight from the database with
 * no validation on write — an arbitrary string must never surface in the
 * document as though it were a section id.
 */
const ALL_SECTION_IDS: LandAnalysisSectionId[] = [
  'summary',
  'history',
  'geo',
  'buildings',
  'zoning',
  'restrictions',
  'purchase_market',
  'rental_market',
  'transport',
  'amenities',
  'noise',
  'sunlight',
  'demographics',
  'employment',
  'feasibility',
  'construction_permits',
  'purchase_listings',
  'rental_listings',
  'transactions',
  'zoning_data',
  'taxes',
  'electricity',
  'access',
  'notes',
  'files',
]

/** `summary` is emitted through `identity`, not as a section of its own. */
const SUPPORTED_SECTIONS: LandAnalysisSectionId[] = [
  'summary',
  ...specIds<LandAnalysisSectionId>(LAND_SECTION_SPECS),
]

const num = (v: unknown): number | null =>
  typeof v === 'number' && Number.isFinite(v) ? v : null

const str = (v: unknown): string | null =>
  typeof v === 'string' && v ? v : null

const m2 = (v: unknown, asOf: string): AiHandoffFigure | null => {
  const value = num(v)
  return value === null
    ? null
    : {
        value: Math.round(value * 10) / 10,
        unit: 'm2',
        source: 'registre_foncier',
        as_of: asOf,
      }
}

export const buildLandAiPayload = (
  share: {
    value?: unknown
    section?: unknown
    land_id?: string | number
  },
  opts: { publicUrl: string; asOf: string }
): AiHandoffLandPayload => {
  const { asOf, publicUrl } = opts
  const value = (share.value ?? {}) as Record<string, any>
  const summary = (value.summary ?? {}) as Record<string, unknown>

  // A land share stores its selection as an ARRAY under `section` (singular).
  // Anything else is treated as "nothing selected" rather than trusted.
  const requested = (
    Array.isArray(share.section) ? share.section : []
  ) as LandAnalysisSectionId[]
  const recognised = requested.filter((s) => ALL_SECTION_IDS.includes(s))
  const included = recognised.filter((s) => SUPPORTED_SECTIONS.includes(s))
  const omitted = recognised.filter((s) => !SUPPORTED_SECTIONS.includes(s))

  const coords = summary.geo_center as
    { lat?: unknown; lon?: unknown } | undefined
  const lat = num(coords?.lat)
  const lon = num(coords?.lon)

  const sections = buildSections(value, included, asOf, LAND_SECTION_SPECS)

  return {
    schema_version: '1.0',
    about: aiHandoffAbout('land'),
    artifact: 'land',
    as_of: asOf,
    attribution: {
      source: 'Popety.io',
      url: publicUrl,
      connect_url: AI_HANDOFF_CONNECT_URL,
    },
    sections_included: included,
    sections_omitted: omitted,
    sections_empty: emptySections(included, sections, LAND_SECTION_SPECS),
    // Identity is not gated on `summary` being selected, for the same reason
    // as on the estimate payload: a document that identifies no parcel is less
    // safe to reason about, not more. `land_id` falls back to the share row
    // itself so the subject is always named even when `summary` was not
    // shared — a land share, unlike a building one, writes no `header`.
    identity: {
      land_id: str(summary.land_id) ?? share.land_id?.toString() ?? null,
      egrid: str(summary.egrid_nb),
      parcel_number: str(summary.code_number),
      address_full: str(summary.address_full),
      municipality_name: str(summary.municipality_name),
      coordinates: lat !== null && lon !== null ? { lat, lon } : null,
      area: m2(summary.area, asOf),
      official_area: m2(summary.official_area, asOf),
      main_zone_name: str(summary.main_lupa_name),
      main_zone_type: str(summary.main_lupa_type),
    },
    sections,
  }
}
